import React from 'react';
import { Mic, Sparkles, X } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StarRating } from '@/components/StarRating';
import { twoLineSummary, resolveScore } from '@/lib/reportConstants';
import { PanelRecordingPlayer } from '@/components/PanelRecordingPlayer';

/**
 * Shared Video Bot / Panel response modal matching hireiq-report-mock.html
 * left: transcript + AI summary; right: video + score bar.
 */
export function ReportMediaModal({
  open,
  onOpenChange,
  kind = 'videobot',
  questionIndex,
  durationLabel,
  questionText,
  transcript,
  summary,
  videoUrl,
  score,
  humanScore,
  panel,
  shareToken,
  isDemo,
}) {
  const resolved = resolveScore(humanScore, score);
  const summaryText = twoLineSummary(summary);
  const qTag = kind === 'panel'
    ? 'Panel Recording · Transcript & AI Summary'
    : `Q${questionIndex ?? 1} · ${durationLabel || '—'} · Video Bot Response`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[min(92vh,900px)] max-w-[1080px] overflow-y-auto p-0 sm:rounded-2xl"
        data-testid="report-media-modal"
      >
        <button
          type="button"
          aria-label="Close"
          className="absolute right-4 top-4 z-10 grid h-8 w-8 place-items-center rounded-full border bg-white shadow-sm"
          onClick={() => onOpenChange(false)}
          data-testid="report-media-modal-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="grid gap-6 p-6 md:grid-cols-[40%_60%]">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-primary">{qTag}</p>
            <h3 className="mt-1.5 text-[16px] font-bold leading-snug tracking-tight text-foreground">
              {questionText || (kind === 'panel' ? 'Panel Evaluation recording' : 'Response')}
            </h3>
            <div className="mt-4">
              <h6 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                <Mic className="h-3.5 w-3.5" /> Voice Transcription
              </h6>
              <div className="max-h-96 overflow-auto rounded-xl border bg-secondary/40 p-3 text-[13px] leading-relaxed text-foreground/80 whitespace-pre-wrap">
                {transcript?.trim() || 'No transcript available.'}
              </div>
            </div>
            {summaryText ? (
              <div className="mt-4">
                <h6 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5 text-amber-600" /> AI Summary
                </h6>
                <div className="rounded-xl border border-primary/25 bg-primary/10 p-3 text-[13px] leading-relaxed text-primary">
                  {summaryText}
                </div>
              </div>
            ) : null}
          </div>
          <div className="flex min-w-0 flex-col justify-center">
            <div className="overflow-hidden rounded-[14px] bg-[#0f1729]">
              {kind === 'panel' && panel?.recording_artifact_id && !isDemo ? (
                <PanelRecordingPlayer
                  panelId={panel.id}
                  artifactId={panel.recording_artifact_id}
                  streamUrlPath={(() => {
                    const base = shareToken
                      ? `/client-report/${shareToken}/panels/${panel.id}/recordings/${panel.recording_artifact_id}/stream-url`
                      : `/panels/${panel.id}/recordings/${panel.recording_artifact_id}/stream-url`;
                    if (!shareToken || typeof window === 'undefined') return base;
                    const email = sessionStorage.getItem(`client-${shareToken}`);
                    return email ? `${base}?email=${encodeURIComponent(email)}` : base;
                  })()}
                  className="aspect-video w-full"
                  testId={`report-media-panel-player-${panel.id}`}
                />
              ) : videoUrl ? (
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  playsInline
                  className="aspect-video w-full"
                />
              ) : (
                <div className="grid aspect-video place-items-center text-[13px] text-slate-400">
                  No recording available
                </div>
              )}
            </div>
            {resolved != null && (
              <div className="mt-2.5 flex items-center justify-between text-[12.5px] text-muted-foreground">
                <StarRating score={resolved} testId="report-media-modal-stars" />
                <span className="tabular-nums">{Number(resolved).toFixed(1)} / 5</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
